import type { Uuid } from '@common/types';
import type { GridEntity } from '@sites/entity-core';
import type {
    WebLegacyWidgetEntity,
    WebWidgetModel,
    WidgetMetaData,
    DenormalizedBlockEntity,
    DenormalizedSectionEntity,
    DenormalizedColumnEntity,
    StructureEntityTypes,
    DenormalizedGridEntity,
    WebWidgetEntity,
    // GridEntity,
} from '@web/types';

import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { createId } from '@paralleldrive/cuid2';
import { produce } from 'immer';

import { getUuid } from '@common/helpers';
import { isLegacyWidget, isWidgetEntityType } from '@common/helpers/widget-envelope';

@Injectable({
    providedIn: 'root',
})
export class WebWidgetsFactoryService {
    private readonly translateService: TranslateService = inject(TranslateService);

    public createWidgetHandler(widget: WebWidgetModel, meta?: WidgetMetaData): WebLegacyWidgetEntity {
        if (widget.signalForm) {
            return this.createSignalFormWidget(this.prepareWidget(widget, meta), meta);
        }

        return this.createLegacyWidget(this.prepareWidget(widget, meta));
    }

    /** A fresh widget seeded from its model — only for models that set `createEntity`. */
    public createWidget(widget: WebWidgetModel, parentId: Uuid): WebWidgetEntity {
        return { ...widget.createEntity!(), id: getUuid(), parentId };
    }

    protected createLegacyWidget(widget: WebWidgetModel): WebLegacyWidgetEntity {
        return {
            id: getUuid(),
            createdAt: Date.now(),
            name: widget.name,
            data: widget.data,
            styles: widget.styles,
            mobileStyles: widget.mobileStyles,
        } as WebLegacyWidgetEntity;
    }

    protected createSignalFormWidget(widget: WebWidgetModel, meta?: WidgetMetaData): WebLegacyWidgetEntity {
        return {
            id: getUuid(),
            createdAt: Date.now(),
            name: widget.name,
            data: widget.data,
            styles: widget.styles,
            mobileStyles: widget.mobileStyles,
            ...(meta?.type === 'zero' ? { type: 'zero' as const } : {}),
        } as WebLegacyWidgetEntity;
    }

    public prepareWidget(widget: WebWidgetModel, meta?: WidgetMetaData): WebWidgetModel {
        return {
            ...widget,
            data: this.prepareWidgetData(widget.data, meta),
        };
    }

    private prepareWidgetData(data: WebWidgetModel['data'], meta?: any): WebWidgetModel['data'] {
        if (typeof data !== 'function') {
            return data;
        }

        return data(this.translateService, meta);
    }

    public static copyWidget<T extends { id: Uuid }>(widget: T): T {
        return this.withCreatedNow(
            this.withReissuedElementIds({
                ...widget,
                id: getUuid(),
            })
        );
    }

    /** A copy of a grid-cell widget re-parented onto `gridId`: legacy `structureId`, versioned `parentId`. */
    public static copyWidgetIntoGrid<T extends { id: Uuid }>(widget: T, gridId: Uuid): T {
        return this.copyWidget(
            isLegacyWidget(widget) ? { ...widget, structureId: gridId } : { ...widget, parentId: gridId }
        );
    }

    /**
     * Button items own an `elementId` that the published page renders as a DOM `id` — anchors, analytics and
     * custom code target it. A copy has to get a fresh one, otherwise duplicating a button widget (or a block
     * that contains one) leaves two elements sharing an id on the same page.
     *
     * Non-button widgets are returned untouched, so the widget's shape is never altered.
     */
    private static withReissuedElementIds<T>(widget: T): T {
        const buttons = (widget as any)?.data?.buttons;

        if (!Array.isArray(buttons)) {
            return widget;
        }

        const copy = {
            ...widget,
            data: {
                ...(widget as any).data,
                buttons: buttons.map((button: any) => ({ ...button })),
            },
        };

        this.reissueElementIds(copy);

        return copy;
    }

    /** Blocks, sections and columns have no `name`; versioned entities carry an `entityType`. */
    private static isLegacyWidgetEntity(entity: unknown): entity is WebLegacyWidgetEntity {
        return typeof (entity as { name?: unknown }).name === 'string' && !('entityType' in (entity as object));
    }

    /** In-place variant of {@link withReissuedElementIds}, for immer drafts. */
    private static reissueElementIds(draft: any): void {
        const buttons = draft?.data?.buttons;

        if (!Array.isArray(buttons)) {
            return;
        }

        buttons.forEach((button: any) => {
            if (button?.elementId) {
                button.elementId = createId();
            }
        });
    }

    /** The entity types whose `meta` carries `createdAt` — a copy is a new entity, created now. */
    private static readonly STAMPED_ENTITY_TYPES: ReadonlySet<unknown> = new Set([
        'block',
        'section',
        'column',
        'grid',
        'tab',
    ]);

    /**
     * A copy is a new entity, created now: structural entities and versioned widgets carry `createdAt` in `meta`,
     * a legacy widget at the root.
     */
    private static withCreatedNow<T>(entity: T, createdAt: number = Date.now()): T {
        const envelope = entity as { entityType?: unknown; meta?: object };

        if (this.STAMPED_ENTITY_TYPES.has(envelope.entityType) || isWidgetEntityType(envelope.entityType)) {
            return { ...entity, meta: { ...envelope.meta, createdAt } };
        }

        return this.isLegacyWidgetEntity(entity) ? { ...entity, createdAt } : entity;
    }

    public static copyBlock(block: DenormalizedBlockEntity): DenormalizedBlockEntity {
        return this.withCreatedNow({ ...block, id: getUuid() });
    }

    public static copySection(section: DenormalizedSectionEntity): DenormalizedSectionEntity {
        return this.withCreatedNow({ ...section, id: getUuid() });
    }

    public static copyColumn(column: DenormalizedColumnEntity): DenormalizedColumnEntity {
        return this.withCreatedNow({ ...column, id: getUuid() });
    }

    public static copyGrid(grid: DenormalizedGridEntity): DenormalizedGridEntity {
        return this.withCreatedNow({ ...grid, id: getUuid() });
    }

    public static copyGridEntity(grid: GridEntity): GridEntity {
        return this.withCreatedNow({ ...grid, id: getUuid() });
    }

    public static getChildEntityKeyNames<T extends StructureEntityTypes>(entity: T) {
        // A migrated entity keeps its children under `children`. Every one of them
        // links back through `parentId` — except the block, which sits on the page
        // and has no parent at all, so it must still be recursed into.
        if ('children' in entity) {
            return { childKey: 'children', parentKey: 'parentId' in entity ? 'parentId' : null };
        }
        if ('sections' in entity) {
            return { childKey: 'sections', parentKey: null };
        }
        if ('columns' in entity) {
            return { childKey: 'columns', parentKey: 'blockId' };
        }
        if ('widgets' in entity) {
            if ('sectionId' in entity) {
                return { childKey: 'widgets', parentKey: 'sectionId' };
            }

            return { childKey: 'widgets', parentKey: 'columnId' };
        }
        // A versioned widget is a leaf with a single parent link.
        if ('parentId' in entity) {
            return { childKey: null, parentKey: 'parentId' };
        }

        // A widget inside a grid is linked by `structureId`; a widget sitting
        // directly in a column by `columnId`.
        return {
            childKey: null,
            parentKey: (entity as { structureId?: Uuid }).structureId ? 'structureId' : 'columnId',
        };
    }

    // use this method to recursive copy blocks, sections, columns, widgets;
    // the whole copied subtree shares one creation time
    public static recursiveEntityCopy<T extends StructureEntityTypes>(
        entity: T,
        parentId?: Uuid,
        createdAt: number = Date.now()
    ): T {
        const { childKey, parentKey } = this.getChildEntityKeyNames<T>(entity);

        return produce<T>(entity, (draft) => {
            if (Array.isArray(draft)) {
                // A grid row: the array holds no id of its own, so the parent id
                // the grid passed down is handed straight to the widgets in it.
                return draft.map((child: T) => this.recursiveEntityCopy(child, parentId, createdAt)) as any;
            } else {
                draft.id = getUuid();
                this.reissueElementIds(draft);

                if (
                    this.STAMPED_ENTITY_TYPES.has((draft as any).entityType) ||
                    isWidgetEntityType((draft as any).entityType)
                ) {
                    (draft as any).meta = { ...(draft as any).meta, createdAt };
                } else if (this.isLegacyWidgetEntity(draft)) {
                    draft.createdAt = createdAt;
                }

                if (parentKey && parentId) {
                    draft[parentKey] = parentId;
                }

                if (draft[childKey]) {
                    const oldChildIds = (draft[childKey] as any[]).map((child) => child?.id);
                    draft[childKey] = draft[childKey].map((child: T) =>
                        this.recursiveEntityCopy(child, draft.id, createdAt)
                    );

                    // Tab widgets reference their content grids by id in two places
                    // (children + props.tabs[].contentEntityId). Keep them in sync after re-id.
                    const tabs = (draft as any).props?.tabs;
                    if (Array.isArray(tabs)) {
                        const newChildIds = (draft[childKey] as any[]).map((child) => child?.id);
                        const idMap = new Map(oldChildIds.map((oldId, index) => [oldId, newChildIds[index]]));
                        (draft as any).props.tabs = tabs.map((tab: any) => ({
                            ...tab,
                            contentEntityId: idMap.get(tab.contentEntityId) ?? tab.contentEntityId,
                        }));
                    }
                }
            }

            return draft;
        });
    }
}
